import { useEffect, useState } from "react";
import api from "@/api/client";
import { Input } from "@/components/ui/input";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { ChevronDown, Search, Check, Type } from "lucide-react";

/**
 * ClientVendorPicker — autocomplete against the tenant's Client/Vendor Master.
 *
 * Feb 8, 2026 — mirrors the UX of RecipientPicker so both directory pickers feel
 * identical to the operator. Free-typing is preserved: the user can type any name
 * (matched or brand-new) and the entry submits normally. New names are auto-saved
 * to the Client/Vendor Master by the backend at parcel-submit time via
 * `_upsert_client_vendor` (see server.py L2780 inward, L3071 outward), so this
 * picker never needs its own explicit "Add" flow.
 *
 * Props:
 *   value          — current { id, name, phone, email, address } (or null)
 *   onChange(rec)  — called with the picked/typed row
 *   kind           — "sender" | "recipient" (filters suggestions)
 *   label          — field label above the trigger
 *   required       — mark asterisk in the label
 *   testid         — prefix for data-testid attrs
 *   placeholder    — placeholder text when nothing chosen
 */
export default function ClientVendorPicker({
  value,
  onChange,
  kind = "both",
  label = "Name",
  required = false,
  testid = "cv-picker",
  placeholder = "Type or pick from master…",
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(value?.name || "");
  const [items, setItems] = useState([]);

  useEffect(() => { setQ(value?.name || ""); }, [value?.name]);

  const load = async (query) => {
    try {
      const { data } = await api.get("/clients-vendors",
        { params: { q: query || undefined, kind, limit: 50 } });
      setItems(data || []);
    } catch { setItems([]); }
  };

  useEffect(() => { if (open) load(q); /* eslint-disable-next-line */ }, [open, q]);

  const commit = (name) => {
    // Free-typed value keeps the parent form working — auto-save happens server-side
    onChange({ id: null, name, phone: value?.phone || "", email: value?.email || "", address: value?.address || "" });
  };

  return (
    <label className="block" data-testid={`${testid}-wrap`}>
      <span className="overline">{label}{required && <span className="text-primary"> *</span>}</span>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div className="relative mt-1">
            <Input
              data-testid={testid}
              value={q}
              onChange={(e) => { setQ(e.target.value); commit(e.target.value); if (!open) setOpen(true); }}
              onFocus={() => setOpen(true)}
              required={required}
              placeholder={placeholder}
              className="rounded-md pr-8"
              autoComplete="off"
            />
            <ChevronDown size={14} className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
          </div>
        </PopoverTrigger>
        <PopoverContent align="start" className="rounded-md p-0 w-[360px]" onOpenAutoFocus={(e) => e.preventDefault()}>
          <div className="p-2 border-b border-border flex items-center gap-2">
            <Search size={14} className="text-muted-foreground" />
            <Input
              data-testid={`${testid}-search`}
              placeholder="Search client / vendor master…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="rounded-md border-0 focus-visible:ring-0 h-8 text-sm"
            />
          </div>
          <div className="max-h-64 overflow-y-auto" data-testid={`${testid}-list`}>
            {items.length === 0 && (
              <div className="p-4 text-xs text-muted-foreground text-center">
                {q ? <>No match. <b>“{q}”</b> will be auto-saved to the Client/Vendor Master after submit.</>
                    : <>Nothing yet. Type a name to search or auto-create.</>}
              </div>
            )}
            {items.map((r) => {
              const selected = value?.id === r.id;
              return (
                <button
                  key={r.id}
                  type="button"
                  data-testid={`${testid}-item-${r.id}`}
                  onClick={() => {
                    onChange({ id: r.id, name: r.name, phone: r.phone || "", email: r.email || "", address: r.address || "" });
                    setQ(r.name);
                    setOpen(false);
                  }}
                  className="w-full text-left px-3 py-2 hover:bg-primary/5 border-b border-border/50 flex items-start justify-between"
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-sm font-semibold truncate">{r.name}</div>
                    <div className="text-[11px] text-muted-foreground mono truncate">
                      {r.phone || "—"}{r.email && ` · ${r.email}`}
                      {typeof r.hit_count === "number" && r.hit_count > 0 && (
                        <span className="ml-1 text-primary/70">· used {r.hit_count}×</span>
                      )}
                    </div>
                  </div>
                  {selected && <Check size={14} className="text-primary mt-1 shrink-0" />}
                </button>
              );
            })}
          </div>
          {q && !items.some(i => i.name.toLowerCase() === q.toLowerCase()) && (
            <div className="border-t border-border px-3 py-2 text-xs bg-primary/5 flex items-center gap-2"
                 data-testid={`${testid}-freetype-hint`}>
              <Type size={12} className="text-primary" />
              <span>Using <b>{q}</b> — will be added to the master when you save.</span>
            </div>
          )}
        </PopoverContent>
      </Popover>
    </label>
  );
}
